/**
 * D-EZ-102 — THE BOOTH WITH A MEMORY.
 *
 * `BoothContext.season` (D-EZ-95) carries what the league knew going into this
 * race: the points leader, the playoff bubble, who won here last time, the
 * defending champion, milestones in play, streaks, rivalries. These are the
 * booth's three uses of it, deliberately sparing — one line before the green,
 * one "points picture" past halfway, one at the flag:
 *
 *   • every line is phrased as how things stood COMING IN, because the context
 *     is pre-race and the booth must never present it as the live standings;
 *   • the finish line combines that pre-race fact with the race's own result
 *     (a first-win chase plus a win IS a first win) — nothing else;
 *   • nothing here invents: a context field that is null says nothing.
 */

import type { BoothSeasonContext } from './context.ts';

const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;

/** One line for the opening beat, or null when the league has nothing to say yet. */
export function openingSeasonLine(
  s: BoothSeasonContext | undefined,
  inRace: (driverId: string) => boolean = () => true,
): string | null {
  if (!s) return null;
  const bits: string[] = [];
  if (s.raceNumber === 1 && s.defendingChampion && inRace(s.defendingChampion.driverId)) {
    bits.push(`${s.defendingChampion.name} starts his title defence as the season ${s.defendingChampion.seasonNumber} champion.`);
  }
  if (s.defendingRaceWinner && inRace(s.defendingRaceWinner.driverId)) {
    bits.push(`${s.defendingRaceWinner.name} won this race last time, in season ${s.defendingRaceWinner.seasonNumber}.`);
  } else {
    const repeat = s.trackWinners.find((t) => t.wins >= 2 && inRace(t.driverId));
    if (repeat) bits.push(`${repeat.name} has won ${plural(repeat.wins, 'time')} at this track.`);
  }
  if (bits.length < 2 && s.pointsLeader && s.raceNumber > 1) {
    bits.push(`${s.pointsLeader.name} comes in leading the standings by ${plural(s.pointsLeader.marginToSecond, 'point')}.`);
  }
  return bits.length ? bits.slice(0, 2).join(' ') : null;
}

/** The mid-race points picture: how the championship stood coming in. */
export function pointsPictureLine(s: BoothSeasonContext | undefined): string | null {
  if (!s) return null;
  const p = s.playoff;
  if (p?.phase === 'regular-season' && p.racesToCut <= 10 && p.cutLine) {
    const short = p.bubble.find((b) => b.side === 'out');
    return `Points picture: coming in, ${p.cutLine.name} held the last playoff spot on points${
      short ? `, with ${short.name} ${plural(-short.gap, 'point')} short` : ''
    } — ${plural(p.racesToCut, 'race')} to the cut, this one included.`;
  }
  if (p?.phase === 'playoff' && p.alive.length > 0) {
    return `Points picture: ${plural(p.alive.length, 'driver')} came in still alive for the title${
      p.cutLine ? `, with ${p.cutLine.name} on the cut line` : ''
    }.`;
  }
  if (s.pointsLeader && s.raceNumber > 1) {
    return `Points picture: ${s.pointsLeader.name} came in leading by ${plural(s.pointsLeader.marginToSecond, 'point')}, with ${plural(
      s.pointsLeader.wins,
      'win',
    )} this season.`;
  }
  return null;
}

/** What the win means, from the pre-race context and the result together. */
export function finishSeasonLine(s: BoothSeasonContext | undefined, winnerId: string, winnerName: string): string | null {
  if (!s) return null;
  const firstWin = s.milestonesInPlay.find((m) => m.driverId === winnerId && m.kind === 'first-win');
  // `n` is the starts he had made before today, so today is start n + 1.
  if (firstWin) return `It is the first win of ${winnerName}'s career, in start number ${firstWin.n + 1}.`;
  const nth = s.milestonesInPlay.find((m) => m.driverId === winnerId && m.kind === 'nth-win');
  if (nth) return `That is career win number ${nth.n} for ${winnerName}.`;
  if (s.defendingRaceWinner?.driverId === winnerId) return `${winnerName} won this race last time too — back to back.`;
  const streak = s.activeStreaks.find((x) => x.driverId === winnerId && x.kind === 'wins');
  if (streak) return `That makes ${streak.length + 1} wins in a row for ${winnerName}.`;
  const drought = s.milestonesInPlay.find((m) => m.driverId === winnerId && m.kind === 'drought');
  if (drought) return `${winnerName} had gone ${drought.n} starts without a win.`;
  return null;
}
